import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";

import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const DAYS = [
  { key: "mon", label: "Lunes" },
  { key: "tue", label: "Martes" },
  { key: "wed", label: "Miercoles" },
  { key: "thu", label: "Jueves" },
  { key: "fri", label: "Viernes" },
  { key: "sat", label: "Sabado" },
  { key: "sun", label: "Domingo" },
];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d.key] = { enabled: d.key !== "sun", from: "09:00", to: "18:00" };
  return acc;
}, {});

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  section: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    transition: "box-shadow .2s",
    "&:hover": {
      boxShadow: `0 4px 20px ${fade(theme.palette.primary.main, 0.08)}`,
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    cursor: "default",
  },
  sectionHeaderClickable: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: fade(theme.palette.primary.main, 0.04),
    },
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sectionBody: {
    padding: theme.spacing(0, 2.5, 2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${fade(theme.palette.primary.main, 0.04)} 0%, ${fade(theme.palette.secondary.main || "#ce93d8", 0.04)} 100%)`,
  },
  toggleInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  usageCard: {
    background: `linear-gradient(135deg, ${fade(theme.palette.primary.main, 0.06)} 0%, ${fade(theme.palette.info?.main || "#29b6f6", 0.06)} 100%)`,
  },
  usageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: theme.spacing(2),
  },
  usageStat: {
    textAlign: "center",
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: fade(theme.palette.background.paper, 0.7),
  },
  usageValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  usageLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginTop: 2,
  },
  progressWrap: {
    marginTop: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: fade(theme.palette.primary.main, 0.12),
  },
  progressFill: {
    borderRadius: 4,
  },
  addTokensRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  textareaWrap: {
    position: "relative",
  },
  textarea: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
  },
  charCount: {
    position: "absolute",
    bottom: 8,
    right: 12,
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
  },
  adminGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
  },
  adminField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  sliderWrap: {
    padding: theme.spacing(0, 1),
  },
  sliderLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  expandIcon: {
    transition: "transform .3s",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },
  saveRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  saveBtn: {
    borderRadius: 12,
    padding: theme.spacing(1, 4),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    boxShadow: `0 4px 14px ${fade(theme.palette.primary.main, 0.35)}`,
    "&:hover": {
      boxShadow: `0 6px 20px ${fade(theme.palette.primary.main, 0.45)}`,
    },
  },
  /* Messages two-column grid */
  messagesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  messageCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  messageLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  /* Schedule day row */
  dayRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${fade(theme.palette.divider, 0.5)}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  dayLabel: {
    width: 90,
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  timeField: {
    width: 100,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
}));


const AISettingsPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usage, setUsage] = useState({ limit: 0, used: 0, remaining: 0 });
  const [addTokens, setAddTokens] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [config, setConfig] = useState({
    isEnabled: true,
    openaiModel: "claude-sonnet-4-20250514",
    openaiTemperature: 0.7,
    openaiMaxTokens: 1000,
    useRag: true,
    ragTopK: 5,
    ragMinScore: 0.7,
    aiPersonality: "",
    aiInstructions: "",
    aiLanguage: "es",
    maxConversationHistory: 10,
    aiWelcomeMessage: "",
    aiFarewellMessage: "",
    aiOffHoursMessage: "",
    aiAvoidTopics: "",
    aiBusinessHours: DEFAULT_HOURS,
  });

  const companyId = useMemo(() => Number(user?.companyId || 0), [user]);
  const canManage = useMemo(() => {
    const profile = String(user?.profile || "").toLowerCase();
    return Boolean(user?.super) || ["admin", "supervisor", "vendedor"].includes(profile);
  }, [user]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/ai/config");
      if (data.aiBusinessHours && typeof data.aiBusinessHours === "object" && Object.keys(data.aiBusinessHours).length > 0) {
        setConfig((p) => ({ ...p, ...data }));
      } else {
        setConfig((p) => ({ ...p, ...data, aiBusinessHours: DEFAULT_HOURS }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    if (!companyId) return;
    try {
      setUsageLoading(true);
      const { data } = await api.get(`/companies/${companyId}/ai/usage`);
      setUsage({
        limit: Number(data?.limit || 0),
        used: Number(data?.used || 0),
        remaining: Number(data?.remaining || 0),
      });
    } catch {
      /* silent */
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put("/ai/config", {
        isEnabled: !!config.isEnabled,
        openaiModel: config.openaiModel,
        openaiTemperature: Number(config.openaiTemperature),
        openaiMaxTokens: Number(config.openaiMaxTokens),
        useRag: !!config.useRag,
        ragTopK: Number(config.ragTopK),
        ragMinScore: Number(config.ragMinScore),
        aiPersonality: config.aiPersonality,
        aiInstructions: config.aiInstructions,
        aiLanguage: config.aiLanguage,
        maxConversationHistory: Number(config.maxConversationHistory),
        aiWelcomeMessage: config.aiWelcomeMessage,
        aiFarewellMessage: config.aiFarewellMessage,
        aiOffHoursMessage: config.aiOffHoursMessage,
        aiAvoidTopics: config.aiAvoidTopics,
        aiBusinessHours: config.aiBusinessHours,
      });
      toast.success(i18n.t("prompts.aiSettings.toasts.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!companyId) return;
    const tokens = Number(addTokens || 0);
    if (!tokens || tokens <= 0) {
      toast.error(i18n.t("prompts.aiSettings.toasts.tokensInvalid"));
      return;
    }
    try {
      setUsageLoading(true);
      await api.post(`/companies/${companyId}/ai/credits`, { tokens });
      setAddTokens("");
      await fetchUsage();
      toast.success(i18n.t("prompts.aiSettings.toasts.tokensAdded"));
    } catch (err) {
      toastError(err);
    } finally {
      setUsageLoading(false);
    }
  };

  const updateBusinessHour = (dayKey, field, value) => {
    setConfig((p) => ({
      ...p,
      aiBusinessHours: {
        ...p.aiBusinessHours,
        [dayKey]: { ...(p.aiBusinessHours?.[dayKey] || {}), [field]: value },
      },
    }));
  };

  const usagePct = usage.limit > 0 ? Math.min((usage.used / usage.limit) * 100, 100) : 0;
  const progressColor = usagePct > 85 ? "#e53935" : usagePct > 60 ? "#fb8c00" : "#43a047";

  return (
    <div className={classes.root}>

      {/* 1. AI Toggle */}
      <div className={classes.toggleRow}>
        <div className={classes.toggleInfo}>
          <div
            className={classes.statusDot}
            style={{ backgroundColor: config.isEnabled ? "#43a047" : "#bdbdbd" }}
          />
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Asistente de IA
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {config.isEnabled ? "Respondiendo conversaciones" : "Pausado"}
            </Typography>
          </div>
        </div>
        <Switch
          color="primary"
          checked={!!config.isEnabled}
          onChange={(e) => setConfig((p) => ({ ...p, isEnabled: e.target.checked }))}
          disabled={!canManage}
        />
      </div>

      {/* 2. Usage / Tokens */}
      <Paper className={`${classes.section} ${classes.usageCard}`} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#1976d2", 0.1) }}>
            <TrendingUpIcon style={{ color: "#1976d2" }} />
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Consumo de tokens
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Uso actual del plan
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.usageGrid}>
            <div className={classes.usageStat}>
              <Typography className={classes.usageValue} style={{ color: "#1976d2" }}>
                {usageLoading ? "..." : usage.limit.toLocaleString()}
              </Typography>
              <Typography className={classes.usageLabel}>
                {i18n.t("prompts.aiSettings.usage.limit")}
              </Typography>
            </div>
            <div className={classes.usageStat}>
              <Typography className={classes.usageValue} style={{ color: progressColor }}>
                {usageLoading ? "..." : usage.used.toLocaleString()}
              </Typography>
              <Typography className={classes.usageLabel}>
                {i18n.t("prompts.aiSettings.usage.used")}
              </Typography>
            </div>
            <div className={classes.usageStat}>
              <Typography className={classes.usageValue} style={{ color: "#43a047" }}>
                {usageLoading ? "..." : usage.remaining.toLocaleString()}
              </Typography>
              <Typography className={classes.usageLabel}>
                {i18n.t("prompts.aiSettings.usage.remaining")}
              </Typography>
            </div>
          </div>

          <div className={classes.progressWrap}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="textSecondary">
                Progreso de uso
              </Typography>
              <Typography variant="caption" style={{ fontWeight: 600, color: progressColor }}>
                {usagePct.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={usagePct}
              className={classes.progressBar}
              classes={{ bar: classes.progressFill }}
            />
          </div>

          {canManage && (
            <div className={classes.addTokensRow}>
              <TextField
                size="small"
                variant="outlined"
                label={i18n.t("prompts.aiSettings.usage.addTokens")}
                type="number"
                value={addTokens}
                onChange={(e) => setAddTokens(e.target.value)}
                inputProps={{ min: "1" }}
                style={{ flex: 1 }}
                InputProps={{ style: { borderRadius: 10 } }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddCredits}
                disabled={usageLoading}
                style={{ borderRadius: 10, textTransform: "none" }}
              >
                {i18n.t("prompts.aiSettings.buttons.addTokens")}
              </Button>
            </div>
          )}
        </div>
      </Paper>

      {/* 3. Personality */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#9c27b0", 0.1) }}>
            <span role="img" aria-label="brain" style={{ fontSize: 20 }}>&#129504;</span>
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("prompts.aiSettings.form.personality")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Define como se presenta tu asistente
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.textareaWrap}>
            <TextField
              className={classes.textarea}
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={config.aiPersonality || ""}
              onChange={(e) => setConfig((p) => ({ ...p, aiPersonality: e.target.value }))}
              placeholder="Ej: Soy un asistente de ventas amigable y profesional que ayuda a los clientes a encontrar el producto ideal."
            />
            <span className={classes.charCount}>
              {(config.aiPersonality || "").length} caracteres
            </span>
          </div>
        </div>
      </Paper>

      {/* 4. Instructions */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#ff9800", 0.1) }}>
            <span role="img" aria-label="rules" style={{ fontSize: 20 }}>&#128221;</span>
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("prompts.aiSettings.form.instructions")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Reglas y comportamiento del asistente
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.textareaWrap}>
            <TextField
              className={classes.textarea}
              variant="outlined"
              fullWidth
              multiline
              rows={6}
              value={config.aiInstructions || ""}
              onChange={(e) => setConfig((p) => ({ ...p, aiInstructions: e.target.value }))}
              placeholder="Ej: Siempre saluda al cliente por su nombre. Ofrece los productos del catalogo. Si pregunta por precios, envia la lista."
            />
            <span className={classes.charCount}>
              {(config.aiInstructions || "").length} caracteres
            </span>
          </div>
        </div>
      </Paper>

      {/* 5. Messages: Welcome, Farewell, Off-hours */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#00bcd4", 0.1) }}>
            <span role="img" aria-label="chat" style={{ fontSize: 20 }}>&#128172;</span>
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Mensajes automaticos
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Bienvenida, despedida y fuera de horario
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.messagesGrid}>
            {/* Welcome */}
            <div className={classes.messageCard} style={{ borderLeftColor: "#43a047", borderLeftWidth: 3 }}>
              <div className={classes.messageLabel}>
                <span style={{ fontSize: 18 }}>&#128075;</span>
                Mensaje de bienvenida
              </div>
              <TextField
                className={classes.textarea}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                size="small"
                value={config.aiWelcomeMessage || ""}
                onChange={(e) => setConfig((p) => ({ ...p, aiWelcomeMessage: e.target.value }))}
                placeholder="Ej: Hola! Bienvenido a nuestro negocio. En que puedo ayudarte?"
              />
            </div>
            {/* Farewell */}
            <div className={classes.messageCard} style={{ borderLeftColor: "#1976d2", borderLeftWidth: 3 }}>
              <div className={classes.messageLabel}>
                <span style={{ fontSize: 18 }}>&#128522;</span>
                Mensaje de despedida
              </div>
              <TextField
                className={classes.textarea}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                size="small"
                value={config.aiFarewellMessage || ""}
                onChange={(e) => setConfig((p) => ({ ...p, aiFarewellMessage: e.target.value }))}
                placeholder="Ej: Gracias por contactarnos! Que tengas un excelente dia."
              />
            </div>
          </div>
          {/* Off-hours - full width */}
          <div className={classes.messageCard} style={{ borderLeftColor: "#ff9800", borderLeftWidth: 3 }}>
            <div className={classes.messageLabel}>
              <span style={{ fontSize: 18 }}>&#127769;</span>
              Mensaje fuera de horario
            </div>
            <TextField
              className={classes.textarea}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={config.aiOffHoursMessage || ""}
              onChange={(e) => setConfig((p) => ({ ...p, aiOffHoursMessage: e.target.value }))}
              placeholder="Ej: En este momento estamos fuera de horario. Te responderemos al iniciar nuestra jornada."
            />
          </div>
        </div>
      </Paper>

      {/* 6. Topics to avoid */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#e53935", 0.1) }}>
            <span role="img" aria-label="block" style={{ fontSize: 20 }}>&#128683;</span>
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Temas a evitar
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Temas que el asistente no debe responder o debe redirigir
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <div className={classes.textareaWrap}>
            <TextField
              className={classes.textarea}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={config.aiAvoidTopics || ""}
              onChange={(e) => setConfig((p) => ({ ...p, aiAvoidTopics: e.target.value }))}
              placeholder="Ej: No hablar de politica, religion, competencia. Si preguntan por servicios que no ofrecemos, redirigir amablemente a lo que si tenemos."
            />
            <span className={classes.charCount}>
              {(config.aiAvoidTopics || "").length} caracteres
            </span>
          </div>
        </div>
      </Paper>

      {/* 7. Business Hours (collapsible) */}
      <Paper className={classes.section} elevation={0}>
        <div
          className={`${classes.sectionHeader} ${classes.sectionHeaderClickable}`}
          onClick={() => setScheduleOpen((o) => !o)}
        >
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#3f51b5", 0.1) }}>
            <ScheduleIcon style={{ color: "#3f51b5" }} />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Horario de atencion
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Define cuando responde el asistente
            </Typography>
          </div>
          <IconButton size="small">
            <ExpandMoreIcon
              className={`${classes.expandIcon} ${scheduleOpen ? classes.expandIconOpen : ""}`}
            />
          </IconButton>
        </div>
        <Collapse in={scheduleOpen}>
          <div className={classes.sectionBody}>
            {DAYS.map((day) => {
              const dayConf = config.aiBusinessHours?.[day.key] || { enabled: false, from: "09:00", to: "18:00" };
              return (
                <div key={day.key} className={classes.dayRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={!!dayConf.enabled}
                        onChange={(e) => updateBusinessHour(day.key, "enabled", e.target.checked)}
                      />
                    }
                    label=""
                    style={{ marginRight: 0 }}
                  />
                  <Typography
                    className={classes.dayLabel}
                    style={{ color: dayConf.enabled ? "inherit" : "#bdbdbd" }}
                  >
                    {day.label}
                  </Typography>
                  {dayConf.enabled ? (
                    <>
                      <TextField
                        className={classes.timeField}
                        variant="outlined"
                        size="small"
                        type="time"
                        value={dayConf.from || "09:00"}
                        onChange={(e) => updateBusinessHour(day.key, "from", e.target.value)}
                      />
                      <Typography variant="body2" color="textSecondary">a</Typography>
                      <TextField
                        className={classes.timeField}
                        variant="outlined"
                        size="small"
                        type="time"
                        value={dayConf.to || "18:00"}
                        onChange={(e) => updateBusinessHour(day.key, "to", e.target.value)}
                      />
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                      Cerrado
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </Collapse>
      </Paper>

      {/* 8. Meta 24h Policy */}
      <Paper className={classes.section} elevation={0}>
        <div className={classes.sectionHeader}>
          <div className={classes.sectionIcon} style={{ backgroundColor: fade("#1565c0", 0.1) }}>
            <span role="img" aria-label="policy" style={{ fontSize: 20 }}>&#128274;</span>
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Politica de 24 horas (Meta)
            </Typography>
            <Typography variant="caption" color="textSecondary">
              WhatsApp, Facebook e Instagram
            </Typography>
          </div>
        </div>
        <div className={classes.sectionBody}>
          <Box
            p={2}
            borderRadius={12}
            border={1}
            borderColor="divider"
            style={{ backgroundColor: fade("#1565c0", 0.03) }}
          >
            <Typography variant="body2" style={{ lineHeight: 1.7 }}>
              Segun la politica de Meta, solo puedes enviar mensajes a un cliente dentro de las
              <strong> 24 horas</strong> posteriores a su ultimo mensaje. Despues de ese periodo,
              la ventana se cierra y el cliente debe escribir primero para reabrir la conversacion.
            </Typography>
            <Box mt={1.5} display="flex" flexDirection="column" style={{ gap: 6 }}>
              <Typography variant="caption" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#43a047", display: "inline-block" }} />
                <strong>Ventana abierta:</strong> Puedes enviar mensajes libremente
              </Typography>
              <Typography variant="caption" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#fb8c00", display: "inline-block" }} />
                <strong>Menos de 4 horas:</strong> La ventana esta por cerrarse
              </Typography>
              <Typography variant="caption" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#e53935", display: "inline-block" }} />
                <strong>Ventana cerrada:</strong> No puedes enviar mensajes hasta que el cliente escriba
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary">
            Esta politica se aplica automaticamente a todos los chats de WhatsApp, Facebook Messenger e Instagram Direct de tus clientes.
          </Typography>
        </div>
      </Paper>

      {/* 9. Admin / Advanced (collapsible) */}
      {canManage && (
        <Paper className={classes.section} elevation={0}>
          <div
            className={`${classes.sectionHeader} ${classes.sectionHeaderClickable}`}
            onClick={() => setAdminOpen((o) => !o)}
          >
            <div className={classes.sectionIcon} style={{ backgroundColor: fade("#607d8b", 0.1) }}>
              <SettingsIcon style={{ color: "#607d8b" }} />
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Configuracion avanzada
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Modelo, temperatura, RAG y mas
              </Typography>
            </div>
            <IconButton size="small">
              <ExpandMoreIcon
                className={`${classes.expandIcon} ${adminOpen ? classes.expandIconOpen : ""}`}
              />
            </IconButton>
          </div>

          <Collapse in={adminOpen}>
            <div className={classes.sectionBody}>
              <div className={classes.adminGrid}>
                <TextField
                  className={classes.adminField}
                  label={i18n.t("prompts.aiSettings.form.openaiModel")}
                  variant="outlined"
                  size="small"
                  select
                  fullWidth
                  value={config.openaiModel || "claude-sonnet-4-20250514"}
                  onChange={(e) => setConfig((p) => ({ ...p, openaiModel: e.target.value }))}
                >
                  <MenuItem value="claude-sonnet-4-20250514">Claude Sonnet 4 (recomendado)</MenuItem>
                  <MenuItem value="claude-opus-4-20250514">Claude Opus 4</MenuItem>
                  <MenuItem value="claude-haiku-4-20250414">Claude Haiku 4</MenuItem>
                  <MenuItem value="gpt-4-turbo-preview">GPT-4 Turbo</MenuItem>
                  <MenuItem value="gpt-4o">GPT-4o</MenuItem>
                  <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                </TextField>

                <TextField
                  className={classes.adminField}
                  label={i18n.t("prompts.aiSettings.form.openaiMaxTokens")}
                  variant="outlined"
                  size="small"
                  type="number"
                  fullWidth
                  value={config.openaiMaxTokens ?? 1000}
                  onChange={(e) => setConfig((p) => ({ ...p, openaiMaxTokens: e.target.value }))}
                />

                <TextField
                  className={classes.adminField}
                  label={i18n.t("prompts.aiSettings.form.maxHistory")}
                  variant="outlined"
                  size="small"
                  type="number"
                  fullWidth
                  value={config.maxConversationHistory ?? 10}
                  onChange={(e) => setConfig((p) => ({ ...p, maxConversationHistory: e.target.value }))}
                />
              </div>

              <div className={classes.sliderWrap}>
                <div className={classes.sliderLabel}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {i18n.t("prompts.aiSettings.form.openaiTemperature")}
                  </Typography>
                  <Chip
                    label={Number(config.openaiTemperature ?? 0.7).toFixed(1)}
                    size="small"
                    variant="outlined"
                    style={{ height: 22, fontSize: "0.75rem" }}
                  />
                </div>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                  <Typography variant="caption" color="textSecondary">Preciso</Typography>
                  <Slider
                    value={Number(config.openaiTemperature ?? 0.7)}
                    onChange={(_, v) => setConfig((p) => ({ ...p, openaiTemperature: v }))}
                    min={0}
                    max={1}
                    step={0.1}
                    color="primary"
                  />
                  <Typography variant="caption" color="textSecondary">Creativo</Typography>
                </Box>
              </div>

              <Box
                p={2}
                borderRadius={12}
                border={1}
                borderColor="divider"
                style={{ backgroundColor: fade("#4caf50", 0.03) }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!config.useRag}
                      onChange={(e) => setConfig((p) => ({ ...p, useRag: e.target.checked }))}
                    />
                  }
                  label={
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {i18n.t("prompts.aiSettings.form.useRag")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Usa tu base de conocimiento para respuestas mas precisas
                      </Typography>
                    </div>
                  }
                />
                <Collapse in={!!config.useRag}>
                  <div className={classes.adminGrid} style={{ marginTop: 12 }}>
                    <TextField
                      className={classes.adminField}
                      label={i18n.t("prompts.aiSettings.form.ragTopK")}
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                      value={config.ragTopK ?? 5}
                      onChange={(e) => setConfig((p) => ({ ...p, ragTopK: e.target.value }))}
                    />
                    <TextField
                      className={classes.adminField}
                      label={i18n.t("prompts.aiSettings.form.ragMinScore")}
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ step: "0.05", min: "0", max: "1" }}
                      fullWidth
                      value={config.ragMinScore ?? 0.7}
                      onChange={(e) => setConfig((p) => ({ ...p, ragMinScore: e.target.value }))}
                    />
                  </div>
                </Collapse>
              </Box>

              <TextField
                className={classes.adminField}
                label={i18n.t("prompts.aiSettings.form.language")}
                variant="outlined"
                size="small"
                fullWidth
                value={config.aiLanguage || "es"}
                onChange={(e) => setConfig((p) => ({ ...p, aiLanguage: e.target.value }))}
              />
            </div>
          </Collapse>
        </Paper>
      )}

      {/* Save */}
      <div className={classes.saveRow}>
        <Button
          className={classes.saveBtn}
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
          size="large"
        >
          {loading ? "Guardando..." : i18n.t("prompts.aiSettings.buttons.save")}
        </Button>
      </div>
    </div>
  );
};

export default AISettingsPanel;
